import { Messages } from '../../codegen/Localize';
import { IActionContext } from '../helpers/ActionContext';
import { FlowErrorCode, MetricErrorCode, ProjectErrorCode, SchedulingErrorCode, ViewErrorCode, WorkbookErrorCode, CollectionsErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { Flows } from '../resources/Flows';
import { Groups } from '../resources/Groups';
import { Metrics } from '../../metrics/resources/Metrics';
import { Projects } from '../resources/Projects';
import { PublishedConnections } from '../resources/PublishedConnections';
import { Schedules } from '../resources/Schedules';
import { Views } from '../resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ToasterService, ToastType } from './ToasterService';
import { CollectionsResourceQuery } from '../../app/spaces/Collections/Collections';
import { IContentInfo } from '../models/IContentInfo';
import { ExplorableItemType } from '../models/ContentType';
import { COLLECTION_NAME_MAX_LENGTH } from '../../app/spaces/Collections/CollectionHeaderEditableNameComponent';

export interface IRenameDialogScope extends ng.IScope {
  description: string;
  input: {
    text: string;
    maxLength?: number;
  };
}

interface RenameActionParams {
  item: INamedItemInfo;
  actionContext: IActionContext;
  dialogTitle: string;
  dialogDescription: string;
  renameFn: (id: string, name: string) => ng.IPromise<IResult>;
  getSuccessMessage: (newName: string) => string;
  defaultErrorMessage: string;
  maxNameLength?: number;
}

export class RenameAction {

  public static $inject = [
    'ConfirmActionDialog',
    'Flows',
    'Projects',
    'Schedules',
    'Views',
    'Workbooks',
    'Metrics',
    'Groups',
    'PublishedConnections'
  ];

  constructor(
    private ConfirmActionDialog: ConfirmActionDialog,
    private Flows: Flows,
    private Projects: Projects,
    private Schedules: Schedules,
    private Views: Views,
    private Workbooks: Workbooks,
    private Metrics: Metrics,
    private Groups: Groups,
    private PublishedConnections: PublishedConnections) { }

  public renamePublishedConnection(publishedConnection: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: publishedConnection,
      actionContext: actionContext,
      dialogTitle: Messages.renamePublishedConnection(),
      dialogDescription: Messages.confirmRenamePublishedConnection({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) }),
      renameFn: (id: string, name: string) => this.PublishedConnections.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.publishedConnectionRenamed({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.publishedConnectionRenameError({ PUBLISHED_CONNECTION_NAME: L10n.elideIfLong(publishedConnection.name) })
    });
  }

  public renameProject(project: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: project,
      actionContext: actionContext,
      dialogTitle: Messages.renameProject(),
      dialogDescription: Messages.confirmRenameProject({ PROJECT_NAME: L10n.elideIfLong(project.name) }),
      renameFn: (id: string, name: string) => this.Projects.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.projectRenamed({ PROJECT_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.projectRenameError({ PROJECT_NAME: L10n.elideIfLong(project.name) })
    });
  }

  public renameWorkbook(workbook: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: workbook,
      actionContext: actionContext,
      dialogTitle: Messages.renameWorkbook(),
      dialogDescription: Messages.confirmRenameWorkbook(),
      renameFn: (id: string, name: string) => this.Workbooks.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.workbookRenamed({ WORKBOOK_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.workbookRenameError({ WORKBOOK_NAME: L10n.elideIfLong(workbook.name) })
    });
  }

  public renameView(view: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: view,
      actionContext: actionContext,
      dialogTitle: Messages.renameView(),
      dialogDescription: Messages.confirmRenameView(),
      renameFn: (id: string, name: string) => this.Views.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.viewRenamed({ VIEW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.viewRenameError({ VIEW_NAME: L10n.elideIfLong(view.name) })
    });
  }

  public renameSchedule(schedule: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: schedule,
      actionContext: actionContext,
      dialogTitle: Messages.renameSchedule(),
      dialogDescription: Messages.confirmRenameSchedule({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) }),
      renameFn: (id: string, name: string) => this.Schedules.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.updateScheduleNameSuccess({ ITEM_NAME: schedule.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.scheduleRenameError({ SCHEDULE_NAME: L10n.elideIfLong(schedule.name) })
    });
  }

  public renameFlow(flow: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: flow,
      actionContext: actionContext,
      dialogTitle: Messages.renameFlow(),
      dialogDescription: Messages.confirmRenameFlow({ FLOW_NAME: L10n.elideIfLong(flow.name) }),
      renameFn: (id: string, name: string) => this.Flows.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.flowRenamed({ FLOW_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.flowRenameError({ FLOW_NAME: L10n.elideIfLong(flow.name) })
    });
  }

  public renameMetric(metric: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: metric,
      actionContext: actionContext,
      dialogTitle: Messages.renameMetric(),
      dialogDescription: Messages.confirmRenameMetric({ METRIC_NAME: L10n.elideIfLong(metric.name) }),
      renameFn: (id: string, name: string) => this.Metrics.rename(id, name),
      getSuccessMessage: (newName: string) => Messages.metricRenamed({ METRIC_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.metricRenameError({ METRIC_NAME: L10n.elideIfLong(metric.name) })
    });
  }

  public renameCollection(collection: IContentInfo<ExplorableItemType>, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: collection,
      actionContext: actionContext,
      dialogTitle: Messages.renameCollection(),
      dialogDescription: Messages.confirmRenameCollection({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      renameFn: (id: string, name: string) => {
        return (new CollectionsResourceQuery).update(collection.luid, {name: name})
        .then(() => {
          return {} as IResult;
        })
        .catch((error: any) => {
          return {errors: [{code: error.data.errorCode, id: collection.id}]} as IResult;
        });
      },
      getSuccessMessage: (newName: string) => Messages.collectionRenamed({ COLLECTION_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.collectionRenameError({ COLLECTION_NAME: L10n.elideIfLong(collection.name) }),
      maxNameLength: COLLECTION_NAME_MAX_LENGTH,
    });
  }

  public renameGroup(group: INamedItemInfo, actionContext: IActionContext): ng.IPromise<IResult> {
    return this.rename({
      item: group,
      actionContext: actionContext,
      dialogTitle: Messages.renameGroup(),
      dialogDescription: Messages.updateGroupNameDesc({ GROUP_NAME: L10n.elideIfLong(group.name) }),
      renameFn: (id: string, name: string) => this.Groups.updateGroupName(id, name),
      getSuccessMessage: (newName: string) => Messages.updateGroupNameSuccessMsg({ ITEM_NAME: group.name, NEW_ITEM_NAME: L10n.elideIfLong(newName) }),
      defaultErrorMessage: Messages.groupRenameError({ GROUP_NAME: L10n.elideIfLong(group.name) })
    });
  }

  private rename(params: RenameActionParams): ng.IPromise<IResult> {
    const { item, actionContext, dialogTitle, dialogDescription, renameFn, getSuccessMessage, defaultErrorMessage, maxNameLength } = params;

    const scope = <IRenameDialogScope> actionContext.scope.$new();
    scope.description = dialogDescription;
    scope.input = { text: item.name, maxLength: maxNameLength || 255 };

    return this.ConfirmActionDialog.open({
      scope: scope,
      title: dialogTitle,
      bodyTemplateUrl: 'confirmActionDialogInput.html',
      actionName: Messages.rename(),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const newName = scope.input.text;
        return renameFn(item.id, newName)
          .then((result: IResult) => {
            if (result.errors && result.errors.length > 0) {
              const errorMessage = this.getErrorMessage(result.errors[0], item, defaultErrorMessage);
              dialogInstance.showErrorMessage(errorMessage);
            } else {
              const successMessage = getSuccessMessage(newName);
              ToasterService.instance.pop({ type: ToastType.Info, text: successMessage });
            }
          });
      }
    }).result.finally(() => scope.$destroy());
  }

  private getErrorMessage(error: IError, item: INamedItemInfo, defaultErrorMessage: string) {
    switch (error.code) {
      case FlowErrorCode.FlowNameAlreadyExists:
        return Messages.errorFlowNameAlreadyExists();
      case MetricErrorCode.MetricNameAlreadyExists:
        return Messages.errorMetricNameAlreadyExists();
      case ProjectErrorCode.ProjectNameAlreadyExists:
        return Messages.errorProjectNameAlreadyExists();
      case SchedulingErrorCode.SchedulingNameAlreadyExists:
        return Messages.errorScheduleNameAlreadyExists();
      case ViewErrorCode.ViewNameAlreadyExists:
        return Messages.errorViewNameAlreadyExists();
      case WorkbookErrorCode.WorkbookNameAlreadyExists:
        return Messages.errorWorkbookNameAlreadyExists();
      case CollectionsErrorCode.DUPLICATE_KEY:
        return Messages.collectionDuplicateRenameError();
      default:
        return defaultErrorMessage;
    }
  }
}

Module.instance().service('RenameAction', RenameAction);
